import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';

import { ConfirmDialog } from 'components/Operator/OperatorUi';
import { getSupportProfile, SupportProfileSummary } from './supportApi';
import { SignInAgain, useSupportReturn } from './supportUi';

/**
 * YAC-2026-009 PR7b。サポートのセッションの、左のメニューの下のプロフィール（components/layout/Profile の代わり）。
 *
 * サポートのセッションでログアウト（/v1/web/sessions/<sid>/logout）すると、運営のセッションへは戻れない（もう一度ログインが
 * 要る）。そこで、ログアウトのボタンを「運営の画面に戻る」に置き換える（サポートは終わらせない）。
 * メールアドレス（support+…@support.invalid）とパスワードの変更は出さない。
 */
const SupportProfile: React.FC = () => {
  const { t } = useTranslation('layout');
  const [profile, setProfile] = useState<SupportProfileSummary | null>(null);
  const [open, setOpen] = useState(false);
  const { busy, error, signIn, run } = useSupportReturn();

  useEffect(() => {
    let active = true;
    getSupportProfile()
      .then((loaded) => {
        if (active) setProfile(loaded);
      })
      .catch((failure) => console.error('Failed to fetch profile data', failure));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div data-support-profile>
      {/* 名前・役割・会社名は、既存のプロフィールと同じ配色（primary/300 の上の primary/50。5.39）。 */}
      <div className="flex flex-row items-center justify-between rounded-md bg-slate-200 bg-opacity-10 p-2">
        {/* 2026-11-03 UAT A04。通常のプロフィールと同じく、顔のアイコンを外した。 */}
        <div className="flex min-w-0 flex-row items-center">
          <div className="min-w-0">
            <p className="truncate text-body-2 text-primary/50" style={{ maxWidth: '12rem' }}>
              {profile?.name ?? ''}
            </p>
            <p className="text-caption text-primary/50">{t('profile.manager')}</p>
            <p className="text-caption text-primary/50">{profile?.company ?? ''}</p>
          </div>
        </div>
        <button
          type="button"
          className="rounded-md p-1 text-primary/50 hover:opacity-75"
          aria-label={t('support.profile-return-label')}
          title={t('support.profile-return-title')}
          onClick={() => setOpen(true)}
        >
          <ArrowUturnLeftIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      {open ? (
        <ConfirmDialog
          title={t('support.profile-return-title')}
          body={
            <>
              <p>{t('support.profile-return-info')}</p>
              {signIn ? <SignInAgain /> : null}
            </>
          }
          confirmLabel={busy ? t('support.returning') : t('support.return')}
          busy={busy}
          errors={error ? [error] : []}
          onClose={() => setOpen(false)}
          onConfirm={() => run(false)}
        />
      ) : null}
    </div>
  );
};

export default SupportProfile;
